/**
 * Citation Page Resolution
 *
 * A citation carries a page number and a verbatim quote. The two can disagree:
 * the stored page is the page of the *chunk* the quote came from, and a chunk
 * routinely spans several pages, so the sentence can sit one or two pages after
 * the number the user is shown (F13 — cited p.31, sentence on p.33).
 *
 * When the quote is not on the cited page, these helpers find the nearby page
 * that actually contains it. Matching is verbatim and contiguous by design:
 * a running header/footer shares only stop-words with a real quote, so
 * boilerplate can never out-score the passage it is printed beside.
 *
 * The same report showed the other half of the problem — the chunk-rect
 * fallback drawing the running footer as if it were the citation — so the
 * header/footer band test lives here too.
 */

/**
 * Share of the quote that must appear verbatim to justify a page jump.
 *
 * Deliberately 1 — the whole quote. A partial match means the in-page DOM
 * search would probably miss on that page too, so jumping there would strand
 * the reader on a *different* page with no highlight at all: strictly worse
 * than leaving them where the citation pointed.
 */
export const QUOTE_PAGE_MIN_SCORE = 1

/** How far either side of the cited page to look */
export const CANDIDATE_PAGE_RADIUS = 3

/** Hard cap on pages whose text we pull — each one is a pdf.js text-content fetch */
export const MAX_CANDIDATE_PAGES = 6

/** Runs shorter than this are noise (shared stop-word sequences) */
const MIN_RUN_WORDS = 4

/** Cap the window search so a pathological quote can't blow up the loop */
const MAX_RUN_WORDS = 60

// ===================
// Candidate ordering
// ===================

export interface CandidatePageInput {
  citedPage: number
  /** Pages the stored chunk highlight covers — the strongest hints available */
  highlightPages?: number[]
  numPages: number
  radius?: number
  max?: number
}

/**
 * Order the pages worth checking, nearest-first.
 *
 * The cited page is excluded — it is only ever called after the in-page search
 * has already missed there. Highlight pages lead (the extractor put the chunk
 * on them), then a symmetric outward walk, forward page first: prose continues
 * onto the following page far more often than the preceding one.
 *
 * Every candidate — highlight pages included — is clamped to `citedPage ±
 * radius`. A citation is off by a page or two because its chunk straddled a
 * page break; it is never off by fifty. Without the clamp a stale highlight
 * from an earlier click could drag the reader across the document.
 */
export function orderCandidatePages({
  citedPage,
  highlightPages = [],
  numPages,
  radius = CANDIDATE_PAGE_RADIUS,
  max = MAX_CANDIDATE_PAGES,
}: CandidatePageInput): number[] {
  const seen = new Set<number>([citedPage])
  const ordered: number[] = []

  const push = (page: number) => {
    if (page < 1 || page > numPages || seen.has(page)) return
    if (Math.abs(page - citedPage) > radius) return
    seen.add(page)
    ordered.push(page)
  }

  for (const page of [...highlightPages].sort(
    (a, b) => Math.abs(a - citedPage) - Math.abs(b - citedPage)
  )) {
    push(page)
  }

  for (let offset = 1; offset <= radius; offset++) {
    push(citedPage + offset)
    push(citedPage - offset)
  }

  return ordered.slice(0, max)
}

// ===================
// Scoring
// ===================

function normalize(text: string): string {
  return text.replace(/\s+/g, ' ').trim().toLowerCase()
}

/**
 * How much of `quote` appears verbatim and contiguous in `pageText`, as a
 * fraction of the quote's word count. 1 means the whole quote is present.
 *
 * Contiguity is the guard against boilerplate: a page footer can share a dozen
 * scattered words with a quote and still score 0, because no run of four
 * consecutive quote words appears in it.
 */
export function quoteMatchScore(pageText: string, quote: string): number {
  const haystack = normalize(pageText)
  const normQuote = normalize(quote)
  if (!haystack || !normQuote) return 0

  if (haystack.includes(normQuote)) return 1

  const words = normQuote.split(' ').filter(Boolean)
  if (words.length === 0) return 0

  const maxWindow = Math.min(words.length, MAX_RUN_WORDS)
  for (let n = maxWindow; n >= MIN_RUN_WORDS; n--) {
    for (let i = 0; i + n <= words.length; i++) {
      if (haystack.includes(words.slice(i, i + n).join(' '))) {
        return n / words.length
      }
    }
  }

  return 0
}

// ===================
// Selection
// ===================

export interface CandidatePageText {
  pageNumber: number
  text: string
}

/**
 * Pick the candidate page that best contains the quote, or null when none is
 * convincing enough to move the user off the page the citation named.
 *
 * Ties break toward the cited page — if two pages carry the sentence verbatim
 * (repeated schedules, annexures), the one the extractor pointed at wins.
 */
export function pickQuotePage(
  candidates: CandidatePageText[],
  quote: string,
  citedPage: number,
  minScore: number = QUOTE_PAGE_MIN_SCORE
): number | null {
  let best: { pageNumber: number; score: number } | null = null

  for (const candidate of candidates) {
    const score = quoteMatchScore(candidate.text, quote)
    if (score < minScore) continue

    if (
      !best ||
      score > best.score ||
      (score === best.score &&
        Math.abs(candidate.pageNumber - citedPage) < Math.abs(best.pageNumber - citedPage))
    ) {
      best = { pageNumber: candidate.pageNumber, score }
    }
  }

  return best?.pageNumber ?? null
}

// ===================
// Running header / footer bands
// ===================

/**
 * Fraction of the page height treated as the running header / footer band.
 * 8% of A4 is ~0.9in — inside the typical 1in margin, so body text does not
 * reach it, but the running title and page number do.
 */
export const RUNNING_BAND_FRACTION = 0.08

/**
 * Is this box entirely inside the running header or footer band?
 *
 * Chunk rects are derived from chunk text, and a chunk that spans a page break
 * swallows the running title/footer of the pages it crosses. Those rects then
 * get drawn as if they were the citation — the F13 failure, where the reader
 * was shown a confidently highlighted page footer. Boilerplate bands are never
 * a meaningful citation target, so they are suppressed.
 *
 * Only fully-contained boxes are dropped: a tall box that merely overlaps the
 * band is real content running into the margin.
 */
export function isRunningBandBox(
  box: { top: number; height: number },
  pageHeight: number,
  band: number = RUNNING_BAND_FRACTION
): boolean {
  if (!pageHeight || pageHeight <= 0) return false
  const bottom = box.top + box.height
  return bottom <= pageHeight * band || box.top >= pageHeight * (1 - band)
}
